/*
 * Price tag builder: live SVG preview of a swap-digit price tag and add-to-cart with the
 * configuration as line item properties. Geometry mirrors the print generator
 * (price-plate.scad in rtw-marketing-automations/print/price-plates): 13.8 x 29.4 mm tiles,
 * 1.7 mm margins, 2 mm side edges. Font lists follow its fonts.json order, so the numbers
 * saved on the order (_build) map straight to build.py --digit-font / --word-font / --name-font.
 */
(function () {
  const FONTS = {
    digits: [
      ['Anton', 'Anton', 400], ['Bebas Neue', 'Bebas Neue', 400], ['League Gothic', 'League Gothic', 400],
      ['Oswald Bold', 'Oswald', 700], ['Fjalla One', 'Fjalla One', 400], ['Big Shoulders', 'Big Shoulders Display', 900],
      ['Roboto Condensed', 'Roboto Condensed', 900], ['Barlow Condensed', 'Barlow Condensed', 800],
      ['Staatliches', 'Staatliches', 400], ['Squada One', 'Squada One', 400],
      ['Satisfy (script)', 'Satisfy', 400], ['Quicksand (rounded)', 'Quicksand', 700],
    ],
    words: [
      ['Archivo Black', 'Archivo Black', 400], ['Montserrat', 'Montserrat', 800], ['Poppins', 'Poppins', 800],
      ['Rubik', 'Rubik', 800], ['Lilita One', 'Lilita One', 400], ['Titan One', 'Titan One', 400],
      ['Passion One', 'Passion One', 700], ['Permanent Marker', 'Permanent Marker', 400], ['Bebas Neue', 'Bebas Neue', 400],
      ['Oswald', 'Oswald', 600], ['Damion (script)', 'Damion', 400, 1.28], ['Quicksand (rounded)', 'Quicksand', 700],
    ],
    names: [
      ['Lato Black', 'Lato', 900], ['Montserrat', 'Montserrat', 700], ['Inter', 'Inter', 800],
      ['Roboto Condensed', 'Roboto Condensed', 700], ['Barlow Semi Condensed', 'Barlow Semi Condensed', 700],
      ['Archivo Narrow', 'Archivo Narrow', 700], ['Oswald', 'Oswald', 500], ['Fira Sans', 'Fira Sans', 800],
      ['Source Sans 3', 'Source Sans 3', 900], ['Nunito', 'Nunito', 900], ['Damion (script)', 'Damion', 400],
      ['Quicksand (rounded)', 'Quicksand', 700],
    ],
  };
  const SIZES = {
    Mini: { dollars: 2, cents: false, qty: false, name: false, set: 16 },
    Classic: { dollars: 2, cents: true, qty: false, name: false, set: 20 },
    Big: { dollars: 3, cents: true, qty: true, name: true, set: 22 },
  };
  // starter kits: one holder per tag so every tag stands or clips on arrival. Colours must be in the
  // section's colour blocks (outdoor kits only use colours marked "Also in ASA").
  const KITS = [
    { name: 'Stall starter', bg: 'starter', blurb: 'Clean, easy to read from across the stall', size: 'Classic', price: '9.95', tags: 5, holders: { Foot: 5 },
      digitFont: 1, plate: 'Ivory White', digits: 'Charcoal', frame: 'Scarlet Red' },
    { name: 'Surplus', bg: 'surplus', blurb: 'Army green on tan with a SURPLUS banner', size: 'Big', price: '49.95', tags: 3, holders: { Foot: 3 },
      digitFont: 1, plate: 'Dark Green', digits: 'Charcoal', frame: 'Desert Tan', banner: 'SURPLUS' },
    { name: 'Sale rack', bg: 'sale', blurb: '"3 for" deals with a SALE banner', size: 'Big', price: '20', qty: '3', tags: 3, holders: { Foot: 3 },
      digitFont: 1, wordFont: 1, plate: 'Lemon Yellow', digits: 'Charcoal', frame: 'Scarlet Red', banner: 'SALE' },
    { name: 'Boutique', bg: 'boutique', blurb: 'Black and gold for jewellery and fashion', size: 'Classic', price: '55.00', tags: 5, holders: { Foot: 5 },
      digitFont: 2, plate: 'Charcoal', digits: 'Desert Tan', frame: 'Desert Tan' },
    { name: 'Wellness', bg: 'wellness', blurb: 'Warm earthy tones for candles, crystals, linen', size: 'Classic', price: '25.50', tags: 5, holders: { Foot: 5 },
      digitFont: 12, plate: 'Ivory White', digits: 'Terracotta', frame: 'Latte Brown' },
    { name: 'Café counter', bg: 'cafe', blurb: 'Chalkboard look for a busy counter', size: 'Classic', price: '5.50', tags: 6, holders: { Foot: 6 },
      digitFont: 2, plate: 'Charcoal', digits: 'Ivory White', frame: 'Latte Brown' },
    { name: 'Cold drinks', bg: 'cold', blurb: 'Fresh ice blue for drinks and gelato', size: 'Mini', price: '10', tags: 6, holders: { Foot: 6 },
      digitFont: 1, plate: 'Ice Blue', digits: 'Charcoal', frame: 'Ivory White' },
    { name: 'Plant stall', bg: 'plant', blurb: 'Outdoor tags for pots and trays', size: 'Mini', price: '12', tags: 8, holders: { 'Plant pick': 8 },
      material: 'Outdoor', digitFont: 2, plate: 'Ivory White', digits: 'Grass Green', frame: 'Grass Green' },
    { name: 'Fruit and veg', bg: 'fruit', blurb: 'Outdoor tags for crates and boxes', size: 'Classic', price: '4.99', tags: 6, holders: { 'Crate clip': 6 },
      material: 'Outdoor', digitFont: 1, plate: 'Ivory White', digits: 'Charcoal', frame: 'Scarlet Red' },
    { name: 'Weekend markets', bg: 'markets', blurb: 'Outdoor Big tags, bold on charcoal', size: 'Big', price: '25', tags: 5, holders: { Foot: 5 },
      material: 'Outdoor', digitFont: 1, plate: 'Charcoal', digits: 'Ivory White', frame: 'Scarlet Red', banner: 'SALE' },
  ];
  // mirrors the Shopify automatic discount "Stall pack special" (admin > Discounts); change both together
  const SPECIAL = { size: 'Classic', min: 5, offEach: 320 };
  // holder interface printed on the plate back (build.py --rail); "slots" until the rail test passes
  const RAIL = 'slots';
  const TILE_W = 13.8, TILE_H = 29.4, CLR = 0.2, MARGIN = 1.7, EDGE = 2, R = 2, WALL = 1.35;
  const GLYPH_W = TILE_W - 1.4, GLYPH_H = 24;
  const NS = 'http://www.w3.org/2000/svg';

  function loadFonts() {
    const fams = {};
    Object.values(FONTS).flat().forEach(([, f, w]) => { (fams[f] = fams[f] || new Set()).add(w); });
    const q = Object.entries(fams).map(([f, ws]) =>
      'family=' + f.replace(/ /g, '+') + ':wght@' + [...ws].sort((a, b) => a - b).join(';')).join('&');
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?' + q + '&display=swap';
    document.head.appendChild(l);
  }

  const sq = (v) => `'${String(v).replace(/'/g, `'\\''`)}'`;     // POSIX single-quote for _build
  function el(tag, attrs, parent) {
    const e = document.createElementNS(NS, tag);
    for (const k in attrs) e.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(e);
    return e;
  }
  // ink box of a string at font-size 100, from canvas metrics (SVG getBBox only gives the line box)
  const ctx = document.createElement('canvas').getContext('2d');
  function ink(str, font) {
    ctx.font = `${font[2]} 100px '${font[1]}'`;
    const m = ctx.measureText(str);
    return { x: -m.actualBoundingBoxLeft, y: -m.actualBoundingBoxAscent,
             w: m.actualBoundingBoxLeft + m.actualBoundingBoxRight, h: m.actualBoundingBoxAscent + m.actualBoundingBoxDescent };
  }
  // text whose ink box is scaled to fit w x h (keeping its aspect), centred on y, anchored by `align`
  function fitText(parent, str, font, w, h, x, y, fill, align) {
    const b = ink(str, font);
    if (!b.w) return { w: 0, h: 0, k: 0, b };
    const k = Math.min(w / b.w, h / b.h), W = b.w * k, H = b.h * k;
    const ox = align === 'left' ? x : align === 'right' ? x - W : x - W / 2;
    const res = { w: W, h: H, k, b };
    if (parent) {
      const t = el('text', { x: ox - b.x * k, y: y - H / 2 - b.y * k, 'font-family': `'${font[1]}'`, 'font-weight': font[2], 'font-size': 100 * k, fill }, parent);
      t.textContent = str; res.el = t;
    }
    return res;
  }
  // text at a given size with the bottom of its ink on baseY
  function onBase(parent, str, font, k, x, baseY, fill, align) {
    const b = ink(str, font), W = b.w * k;
    const ox = align === 'left' ? x : x - W / 2;
    const t = el('text', { x: ox - b.x * k, y: baseY - (b.y + b.h) * k, 'font-family': `'${font[1]}'`, 'font-weight': font[2], 'font-size': 100 * k, fill }, parent);
    t.textContent = str;
    return W;
  }

  class PriceTagBuilder extends HTMLElement {
    connectedCallback() {
      this.cfg = JSON.parse(this.querySelector('[data-config]').textContent);
      this.svg = this.querySelector('svg.ptb-svg');
      this.form = this.querySelector('form');
      this.fillFontSelects();
      this.fillSwatches();
      this.form.addEventListener('input', (e) => { if (e.target.matches('[data-addon]')) this.holdersSet = true; if (e.isTrusted) this.markKit(null); this.update(); });
      this.form.addEventListener('change', (e) => { if (e.isTrusted) this.markKit(null); this.update(); });
      this.buildKits();
      this.querySelectorAll('[data-picker]').forEach((btn) => btn.addEventListener('click', () => this.openPalette(btn.dataset.picker)));
      this.querySelectorAll('[data-swatches] input').forEach((inp) => inp.addEventListener('change', (e) => { if (e.isTrusted) this.openPalette(null); }));
      const list = this.querySelector('[data-kits]');
      const navState = () => {
        const max = list.scrollWidth - list.clientWidth - 2;
        this.querySelectorAll('[data-kit-nav]').forEach((b) => { b.hidden = b.dataset.kitNav < 0 ? list.scrollLeft < 8 : list.scrollLeft > max - 6; });
      };
      if (list) { list.addEventListener('scroll', navState, { passive: true }); window.addEventListener('resize', navState); setTimeout(navState, 300); }
      this.querySelectorAll('[data-kit-nav]').forEach((btn) => btn.addEventListener('click', () => {
        const list = this.querySelector('[data-kits]'), card = list.querySelector('.ptb-kit');
        list.scrollBy({ left: +btn.dataset.kitNav * (card ? card.offsetWidth + 12 : 240) * 2, behavior: 'smooth' });
      }));
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.addToCart(); });
      loadFonts();
      (document.fonts ? document.fonts.ready : Promise.resolve()).then(() => this.refresh());
      this.update();
      setTimeout(() => this.refresh(), 1500);         // late webfonts
    }
    refresh() { this.update(); this.renderKits(); }
    openPalette(name) {
      this.querySelectorAll('[data-picker]').forEach((btn) => {
        const open = btn.dataset.picker === name && btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        this.querySelector('#' + btn.getAttribute('aria-controls')).hidden = !open;
      });
    }
    // holder thumbnails under the preview, one per holder type with its count
    renderHolders() {
      const strip = this.querySelector('[data-holders]');
      if (!strip) return;
      strip.innerHTML = '';
      this.form.querySelectorAll('[data-addon]').forEach((inp) => {
        const n = +inp.value || 0;
        if (n < 1) return;
        const fig = document.createElement('figure');
        fig.className = 'ptb-holder';
        fig.innerHTML = '<img alt="" width="150" height="150"><b></b><figcaption></figcaption>';
        fig.querySelector('img').src = inp.dataset.img;
        fig.querySelector('b').textContent = `\u00d7${n}`;
        fig.querySelector('figcaption').textContent = inp.dataset.addon;
        strip.appendChild(fig);
      });
      strip.hidden = !strip.children.length;
    }
    colour(name) {
      if (name === 'No frame') return { name, hex: 'none' };
      const c = this.cfg.colours.find((x) => x.name === name);
      return c ? { name, hex: c.hex } : null;
    }
    // holders a kit can actually ship: any it lists that aren't on sale become feet
    kitHolders(kit) {
      const live = new Set([...this.form.querySelectorAll('[data-addon]')].map((i) => i.dataset.addon));
      const out = {};
      // a hidden holder becomes a foot; if feet are hidden too, the kit ships without that holder
      Object.entries(kit.holders).forEach(([h, n]) => {
        const k = live.has(h) ? h : live.has('Foot') ? 'Foot' : null;
        if (k) out[k] = (out[k] || 0) + n;
      });
      return out;
    }
    kitState(kit) {
      const S = SIZES[kit.size];
      return { size: kit.size, S, material: kit.material || 'Indoor', price: kit.price, qty: S.qty ? kit.qty || '' : '', name1: '', name2: '',
        digitFont: kit.digitFont || 1, wordFont: kit.wordFont || 1, nameFont: 1,
        plate: this.colour(kit.plate), digits: this.colour(kit.digits), frame: this.colour(kit.frame), banner: kit.banner || '', quantity: kit.tags };
    }
    // a kit is usable only if its colours exist in the section (and in ASA for outdoor kits)
    kitOk(kit) {
      return ['plate', 'digits', 'frame'].every((k) => {
        const c = this.cfg.colours.find((x) => x.name === kit[k]);
        return c && (kit.material !== 'Outdoor' || c.outdoor);
      });
    }
    buildKits() {
      const list = this.querySelector('[data-kits]');
      if (!list) return;
      this.kits = KITS.filter((k) => this.kitOk(k));
      this.kits.forEach((kit, i) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'ptb-kit'; b.dataset.kit = i;
        b.innerHTML = '<svg class="ptb-kit-tag" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"></svg>' +
          `<span class="ptb-kit-top"><b></b>${kit.material === 'Outdoor' ? '<i>Outdoor</i>' : ''}</span><em class="ptb-kit-price"></em><span class="ptb-kit-what"></span>`;
        if (kit.bg && list.dataset.img) b.style.backgroundImage = `url(${list.dataset.img}ptb-kit-${kit.bg}.jpg)`;
        b.querySelector('b').textContent = kit.name;
        b.title = kit.blurb;
        const hold = Object.entries(this.kitHolders(kit)).map(([h, n]) => `${n} ${h.toLowerCase()}${n > 1 ? (h === 'Foot' ? '' : 's') : ''}`).join(', ').replace(/(\d+) foot$/, (m, n) => n > 1 ? `${n} feet` : m);
        b.querySelector('.ptb-kit-what').textContent = `${kit.tags} ${kit.size}${kit.banner ? ' + banners' : ''}${hold ? ', ' + hold : ''}`;
        b.setAttribute('aria-label', `${kit.name}: ${kit.blurb}. ${kit.tags} ${kit.size} tags${hold ? ', ' + hold : ''}.`);
        b.dataset.hold = hold;
        b.addEventListener('click', () => this.applyKit(i));
        list.appendChild(b);
      });
      this.renderKits();
    }
    renderKits() {
      (this.kits || []).forEach((kit, i) => {
        const b = this.querySelector(`[data-kit="${i}"]`), st = this.kitState(kit);
        this.render(st, b.querySelector('svg'), { pad: 1 });
        const r = this.lineItems(st, this.kitHolders(kit));
        const em = b.querySelector('em');
        if (r.error) em.textContent = '';
        else { const sp = this.specialOff(r.items); em.innerHTML = this.priceHtml(this.total(r.items), sp.off); em.classList.toggle('is-special', !!sp.off); }
      });
    }
    markKit(i) { this.querySelectorAll('.ptb-kit').forEach((b) => b.setAttribute('aria-pressed', String(+b.dataset.kit === i))); }
    applyKit(i) {
      const kit = this.kits[i], f = this.form.elements;
      const set = (name, value) => { const r = [...this.form.querySelectorAll(`input[name="${name}"]`)].find((x) => x.value === value); if (r) r.checked = true; return r; };
      set('size', kit.size); set('material', kit.material || 'Indoor');
      f.price.value = kit.price; f.qty_prefix.value = kit.qty || '';
      f.name1.value = ''; f.name2.value = '';
      f.digits_font.value = kit.digitFont || 1; f.words_font.value = kit.wordFont || 1;
      [['plate_colour', kit.plate], ['digit_colour', kit.digits], ['frame_colour', kit.frame]].forEach(([n, v]) => {
        set(n, v); this.querySelector(`[data-swatches="${n}"]`).dataset.wanted = v;
      });
      f.banner.checked = !!kit.banner; f.banner_word.value = kit.banner || 'SALE';
      f.quantity.value = kit.tags;
      const hs = this.kitHolders(kit);
      this.form.querySelectorAll('[data-addon]').forEach((inp) => { inp.value = hs[inp.dataset.addon] || 0; });
      if (f.spare_sets) f.spare_sets.value = 0;
      this.holdersSet = true;
      this.update();
      this.markKit(i);
      const msg = this.querySelector('[data-message]'); if (msg) msg.textContent = '';
      this.querySelector('.ptb-grid').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    fillFontSelects() {
      for (const role of ['digits', 'words', 'names']) {
        const s = this.form.elements[role + '_font'];
        FONTS[role].forEach(([label, fam, w], i) => {
          const o = document.createElement('option');
          o.value = i + 1; o.textContent = label; o.style.fontFamily = `'${fam}'`; o.style.fontWeight = w;
          s.appendChild(o);
        });
      }
    }
    fillSwatches() {
      const cols = this.cfg.colours;
      this.querySelectorAll('[data-swatches]').forEach((box) => {
        const name = box.dataset.swatches, def = box.dataset.default;
        const list = box.dataset.allowNone ? [{ name: 'No frame', hex: 'none', outdoor: true }].concat(cols) : cols;
        list.forEach((c, i) => {
          const id = `${this.id}-${name}-${i}`;
          const lab = document.createElement('label');
          lab.className = 'ptb-swatch';
          lab.title = c.name;
          lab.innerHTML = `<input type="radio" name="${name}" id="${id}">` +
            `<span style="background:${c.hex === 'none' ? 'repeating-linear-gradient(45deg,#fff 0 4px,#ddd 4px 8px)' : c.hex}"></span>`;
          const inp = lab.querySelector('input');
          inp.value = c.name; inp.setAttribute('aria-label', c.name);
          inp.dataset.hex = c.hex; lab.dataset.outdoor = c.outdoor ? '1' : '';
          if (c.name === def || (!list.some((x) => x.name === def) && i === 0)) inp.checked = true;
          inp.addEventListener('change', () => { box.dataset.wanted = c.name; });
          box.appendChild(lab);
        });
      });
    }
    state() {
      const f = this.form.elements, size = f.size.value, S = SIZES[size];
      const pick = (n) => { const r = this.form.querySelector(`input[name="${n}"]:checked`); return r ? { name: r.value, hex: r.dataset.hex } : null; };
      return {
        size, S,
        material: f.material.value,
        price: (f.price.value || '').replace(/[^0-9.]/g, ''),
        qty: S.qty ? f.qty_prefix.value : '',
        ...this.names(S, f),
        digitFont: +f.digits_font.value, wordFont: +f.words_font.value, nameFont: +f.names_font.value,
        plate: pick('plate_colour'), digits: pick('digit_colour'), frame: pick('frame_colour'),
        banner: f.banner.checked && !f.banner.disabled ? (f.banner_word.value.trim().toUpperCase().slice(0, 8) || 'SALE') : '',
        quantity: Math.max(1, +f.quantity.value || 1),
      };
    }
    names(S, f) {
      if (!S.name) return { name1: '', name2: '' };
      const l = [f.name1.value, f.name2.value].map((v) => v.trim().slice(0, 28)).filter(Boolean);
      return { name1: l[0] || '', name2: l[1] || '' };
    }
    // what the price box asks for vs what the size can show; null when it fits as typed
    priceProblem(st) {
      const [d = '', c = ''] = st.price.split('.'), dollars = d.replace(/^0+(?=\d)/, '');
      const max = '$' + '9'.repeat(st.S.dollars) + (st.S.cents ? '.99' : '');
      if (dollars.length > st.S.dollars) return { block: true, text: st.size === 'Big' ? `Big shows up to ${max}, the most our tags fit.` : `${st.size} shows up to ${max}. Pick a bigger size for $${dollars}.` };
      if (!st.S.cents && /[1-9]/.test(c)) return { block: false, text: `Mini shows whole dollars, so this packs as $${dollars || 0}. The cents tiles come with Classic and Big.` };
      if (c.length > 2) return { block: false, text: 'Only two cents digits fit, so we use the first two.' };
      return null;
    }
    // the digits to show in the pockets, right-aligned in the dollar slots
    slots(st) {
      const S = st.S;
      let [d, c = ''] = st.price.split('.');
      d = (d || '').replace(/^0+(?=\d)/, '').slice(-S.dollars);
      c = (c + '00').slice(0, 2);
      const dollars = Array(S.dollars - d.length).fill('').concat(d.split(''));
      return S.cents ? dollars.concat(c.split('')) : dollars;
    }
    // Outdoor (ASA) only offers the colours marked for it; a hidden pick moves to the first one left
    syncSwatches() {
      const outdoor = this.form.elements.material.value === 'Outdoor', swapped = [];
      const boxes = {};
      this.querySelectorAll('[data-swatches]').forEach((box) => {
        boxes[box.dataset.swatches] = box;
        const labs = [...box.querySelectorAll('.ptb-swatch')];
        labs.forEach((l) => { l.hidden = outdoor && !l.dataset.outdoor; });
        // the customer's own pick wins whenever it's offered; otherwise the section default, then the first left
        const offered = (n) => labs.find((l) => !l.hidden && l.querySelector('input').value === n);
        const pick = offered(box.dataset.wanted) || offered(box.dataset.default) || labs.find((l) => !l.hidden);
        if (pick) pick.querySelector('input').checked = true;
        if (pick && box.dataset.wanted && pick.querySelector('input').value !== box.dataset.wanted) swapped.push(`${box.dataset.wanted} isn't available for Outdoor, so we picked ${pick.querySelector('input').value}`);
      });
      // digits must stand out from the plate: fall back to the default digit colour, or the default plate colour if that's the plate
      const val = (n) => boxes[n].querySelector('input:checked').value;
      if (val('digit_colour') === val('plate_colour')) {
        const labs = [...boxes.digit_colour.querySelectorAll('.ptb-swatch')].filter((l) => !l.hidden);
        const alt = [boxes.digit_colour.dataset.default, boxes.plate_colour.dataset.default].map((n) => labs.find((l) => l.querySelector('input').value === n))
          .find((l) => l && l.querySelector('input').value !== val('plate_colour')) || labs.find((l) => l.querySelector('input').value !== val('plate_colour'));
        if (alt) { alt.querySelector('input').checked = true; swapped.push(`digits can't match the plate, so they're ${val('digit_colour')}`); }
      }
      Object.values(boxes).forEach((box) => {
        const label = this.querySelector(`[data-colour-name="${box.dataset.swatches}"]`), cur = box.querySelector('input:checked');
        if (label) label.textContent = cur ? cur.value : '';
        const chip = this.querySelector(`[data-chip="${box.dataset.swatches}"]`);
        if (chip && cur) chip.style.background = cur.dataset.hex === 'none' ? 'repeating-linear-gradient(45deg,#fff 0 3px,#ddd 3px 6px)' : cur.dataset.hex;
      });
      const note = this.querySelector('[data-colour-switch]');
      note.hidden = !swapped.length; note.textContent = swapped.length ? swapped.join('. ').replace(/^./, (c) => c.toUpperCase()) + '.' : '';
      const noFrame = this.form.querySelector('input[name="frame_colour"]:checked').dataset.hex === 'none';
      this.form.elements.banner.disabled = noFrame;
      this.querySelector('[data-banner-hint]').hidden = !noFrame;
    }
    update() {
      this.syncSwatches();
      if (!this.holdersSet) {
        const foot = this.form.querySelector('[data-addon="Foot"]');
        if (foot) foot.value = Math.max(1, +this.form.elements.quantity.value || 1);
      }
      const st = this.state(), S = st.S;
      this.querySelectorAll('[data-show]').forEach((n) => { n.hidden = !S[n.dataset.show]; });
      this.querySelector('[data-banner-word]').hidden = !st.banner;
      const prob = this.priceProblem(st), pw = this.querySelector('[data-price-warning]');
      pw.hidden = !prob; pw.textContent = prob ? prob.text : '';
      this.form.querySelector('[type=submit]').disabled = this.busy || !!(prob && prob.block);
      this.render(st);
      this.renderHolders();
      this.updatePrice(st);
    }
    render(st, svg = this.svg, opt = {}) {
      const S = st.S, main = svg === this.svg;
      svg.innerHTML = '';
      const dF = FONTS.digits[st.digitFont - 1], wF = FONTS.words[st.wordFont - 1], nF = FONTS.names[st.nameFont - 1];
      // canvas metrics silently fall back until a face has loaded, so load what we use and redraw
      if (document.fonts) {
        const waiting = [dF, wF, nF, ['', 'Archivo Black', 400]].map((f) => `${f[2]} 100px '${f[1]}'`)
          .filter((spec) => !document.fonts.check(spec));
        if (waiting.length) Promise.all(waiting.map((spec) => document.fonts.load(spec))).then(() => this.refresh());
      }
      const plate = st.plate.hex, inkC = st.digits.hex, frame = st.frame.hex;
      let maxW = 0, maxH = 0;
      for (let i = 0; i < 10; i++) { const b = ink(String(i), dF); maxW = Math.max(maxW, b.w); maxH = Math.max(maxH, b.h); }
      const k = Math.min(GLYPH_W / maxW, GLYPH_H / maxH) || 0.2;
      const realH = ink('0', dF).h * k;
      const ROW_H = TILE_H + CLR + 2 * MARGIN;
      let prefixW = 0, qM, wM;
      const scale = wF[3] || 1;
      if (st.qty) {
        qM = fitText(null, st.qty, wF, 40, realH, 0, 0);
        wM = fitText(null, 'for', wF, 30 * scale, realH * 0.38 * scale, 0, 0);
        prefixW = qM.w + 1.2 + wM.w + 3.5;
      }
      const sM = fitText(null, '$', dF, 12, Math.min(GLYPH_H, realH * 1.15), 0, 0);
      const dollarW = sM.w + 3;
      const dolW = S.dollars * TILE_W + CLR, dotW = 5, cenW = S.cents ? 2 * TILE_W + CLR : 0;
      const xPref = EDGE, xDol = xPref + prefixW, xDp = xDol + dollarW, xDot = xDp + dolW, xCp = xDot + dotW;
      const W = (S.cents ? xCp + cenW : xDot + 1) + EDGE;
      const lines = [st.name1, st.name2].filter(Boolean);
      let band = 0, nameK = 0, lineH = 0, capK = 0;
      if (lines.length) {
        nameK = 6.5 / ink('H', nF).h;
        lines.forEach((l) => { nameK = Math.min(nameK, (W - 2 * EDGE - 2) / ink(l, nF).w); });
        lineH = ink('Hfgjpy', nF).h * nameK * 1.15; band = lines.length * lineH + 3.2;
        capK = ink('H', nF).h * nameK;
      }
      const H = ROW_H + band;
      const tabH = st.banner ? 11.5 : 0;
      const pad = opt.pad != null ? opt.pad : 6;
      svg.setAttribute('viewBox', `${-WALL - pad} ${-WALL - tabH - pad} ${W + 2 * WALL + 2 * pad} ${H + 2 * WALL + tabH + 2 * pad}`);
      const root = el('g', {}, svg);
      // frame + banner tab
      const fr = frame === 'none' ? null : frame;
      if (fr) {
        if (st.banner) {
          const BF = ['', 'Archivo Black', 400], bi = ink(st.banner, BF);
          const bw = Math.min(W, bi.w * (7.5 / bi.h) + 9);
          el('path', { d: `M${W / 2 - bw / 2 - 3},${0} L${W / 2 + bw / 2 + 3},0 L${W / 2 + bw / 2},${-tabH} L${W / 2 - bw / 2},${-tabH} Z`, fill: fr, 'stroke-linejoin': 'round', stroke: fr, 'stroke-width': 1.2 }, root);
          fitText(root, st.banner, BF, bw - 6, 7.5, W / 2, -tabH / 2 - 0.2, inkC, 'center');
        }
        el('rect', { x: -WALL, y: -WALL, width: W + 2 * WALL, height: H + 2 * WALL, rx: R + WALL, fill: fr }, root);
      }
      // y measured down from the top in SVG; the price row sits at the bottom of the plate
      el('rect', { x: 0, y: 0, width: W, height: H, rx: R, fill: plate, stroke: fr ? 'none' : 'rgba(0,0,0,.25)', 'stroke-width': 0.3 }, root);
      if (fr) el('rect', { x: 0.6, y: 0.6, width: W - 1.2, height: H - 1.2, rx: R - 0.4, fill: 'none', stroke: 'rgba(0,0,0,.18)', 'stroke-width': 0.25 }, root);
      const rowTop = band, cy = rowTop + ROW_H / 2;
      const baseY = cy + realH / 2;                       // digit baseline (bottom of the ink)
      // tiles
      const pos = [];
      for (let i = 0; i < S.dollars; i++) pos.push(xDp + CLR / 2 + i * TILE_W);
      if (S.cents) for (let i = 0; i < 2; i++) pos.push(xCp + CLR / 2 + i * TILE_W);
      const digits = this.slots(st);
      pos.forEach((x, i) => {
        el('rect', { x: x + 0.15, y: rowTop + MARGIN + CLR / 2 + 0.15, width: TILE_W - 0.3, height: TILE_H - 0.3, rx: 0.6, fill: plate, stroke: 'rgba(0,0,0,.22)', 'stroke-width': 0.25 }, root);
        const ch = digits[i];
        if (ch) {
          onBase(root, ch, dF, k, x + TILE_W / 2, baseY, inkC, 'center');
        }
      });
      // $, point, prefix
      fitText(root, '$', dF, 12, Math.min(GLYPH_H, realH * 1.15), xDol + dollarW / 2 - 0.5, cy, inkC, 'center');
      if (S.cents) el('circle', { cx: xDot + dotW / 2, cy: baseY - 1.6, r: 1.6, fill: inkC }, root);
      if (st.qty) {
        const qw = onBase(root, st.qty, wF, qM.k, xPref, baseY, inkC, 'left');
        onBase(root, 'for', wF, wM.k, xPref + qw + 1.2, baseY, inkC, 'left');
      }
      // shared baseline per line, like the SCAD: the bottom of 'Hfgjpy' sits 2.2 + (i+1) * lineH from the top
      const lm = ink('Hfgjpy', nF), desc = (lm.y + lm.h) * nameK;
      lines.forEach((l, i) => {
        const b = ink(l, nF);
        const t = el('text', { x: W / 2 - (b.x + b.w / 2) * nameK, y: 2.2 + (i + 1) * lineH - desc, 'font-family': `'${nF[1]}'`, 'font-weight': nF[2], 'font-size': 100 * nameK, fill: inkC }, root);
        t.textContent = l;
      });
      if (!main) return;
      this.dims = { w: W + 2 * WALL, h: H + 2 * WALL + tabH };
      const d = this.querySelector('[data-dims]');
      if (d) d.textContent = `${Math.round(this.dims.w)} x ${Math.round(this.dims.h)} mm, ${S.set} digit tiles included`;
      const warn = this.querySelector('[data-name-warning]');
      if (warn) warn.hidden = !(lines.length && capK < 4.5);
    }
    variantFor(product, ...opts) {
      if (!product) return null;
      return product.variants.find((v) => opts.every((o, i) => !o || v.options[i] === o)) || null;
    }
    // holders: { title: count } overrides the form (kit cards); without it the form's counts and spares are used
    lineItems(st, holders) {
      const c = this.cfg, items = [];
      const tag = this.variantFor(c.products.tag, st.size, st.material);
      if (!tag) return { error: 'This tag option is not available right now.' };
      const props = {
        'Price shown': st.price ? '$' + this.slots(st).slice(0, st.S.dollars).join('') + (st.S.cents ? '.' + this.slots(st).slice(-2).join('') : '') : '',
        'Digit font': FONTS.digits[st.digitFont - 1][0],
        'Plate colour': st.plate.name, 'Digit colour': st.digits.name, 'Frame colour': st.frame.name,
      };
      if (st.qty) { props['Multi-buy'] = `${st.qty} for`; props['Word font'] = FONTS.words[st.wordFont - 1][0]; }
      if (st.name1) { props['Name'] = [st.name1, st.name2].filter(Boolean).join(' / '); props['Name font'] = FONTS.names[st.nameFont - 1][0]; }
      if (st.banner) props['Banner'] = st.banner;
      const forTag = `${st.size}, ${st.material}`;
      const args = [`--dollars ${st.S.dollars}`, st.S.cents ? '' : '--no-cents', st.qty ? `--qty ${st.qty}` : '',
        st.name1 ? `--name ${sq(st.name1)}` : '', st.name2 ? `--name2 ${sq(st.name2)}` : '',
        `--digit-font ${st.digitFont}`, st.qty ? `--word-font ${st.wordFont}` : '', st.name1 ? `--name-font ${st.nameFont}` : '',
        st.banner ? `--topper ${sq(st.banner)}` : '', `--rail ${RAIL}`, `--kind ${st.size.toLowerCase()}`].filter(Boolean).join(' ');
      props['_build'] = args;
      Object.keys(props).forEach((k) => { if (!props[k]) delete props[k]; });
      items.push({ id: tag.id, quantity: st.quantity, properties: props });
      if (st.banner) {
        const b = c.products.banner && c.products.banner.variants[0];
        if (b) items.push({ id: b.id, quantity: st.quantity, properties: { Word: st.banner, Colour: st.frame.name, For: forTag } });
      }
      const counts = holders || Object.fromEntries([...this.form.querySelectorAll('[data-addon]')].map((inp) => [inp.dataset.addon, +inp.value || 0]));
      Object.entries(counts).forEach(([title, n]) => {
        const v = this.variantFor(c.products.addon, title);
        if (n > 0 && v) items.push({ id: v.id, quantity: n, properties: { Colour: st.frame.hex === 'none' ? st.plate.name : st.frame.name, For: forTag } });
      });
      const spare = holders ? 0 : +this.form.elements.spare_sets.value || 0;
      if (spare > 0 && c.products.spare) items.push({ id: c.products.spare.variants[0].id, quantity: spare, properties: { 'Digit font': FONTS.digits[st.digitFont - 1][0], 'Colours': `${st.digits.name} on ${st.plate.name}`, For: forTag } });
      return { items };
    }
    money(cents) { return this.cfg.money.replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2)); }
    total(items) {
      const price = (id) => { for (const p of Object.values(this.cfg.products)) { if (!p) continue; const v = p.variants.find((x) => x.id === id); if (v) return v.price; } return 0; };
      return items.reduce((s, it) => s + price(it.id) * it.quantity, 0);
    }
    // cents off from the stall special for these line items (Classic tags only, 5 or more)
    specialOff(items) {
      const tag = this.cfg.products.tag;
      if (!tag) return { qty: 0, off: 0 };
      const ids = tag.variants.filter((v) => v.options[0] === SPECIAL.size).map((v) => v.id);
      const qty = items.filter((it) => ids.includes(it.id)).reduce((s, it) => s + it.quantity, 0);
      return { qty, off: qty >= SPECIAL.min ? qty * SPECIAL.offEach : 0 };
    }
    priceHtml(full, off) {
      const esc = (t) => t.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      const short = (c) => esc(this.money(c).replace(/\.00$/, ''));
      return off ? `<s>${short(full)}</s> ${short(full - off)}` : short(full);
    }
    updatePrice(st) {
      const money = (cents) => this.money(cents);
      this.querySelectorAll('[data-size-price]').forEach((n) => {
        const v = this.variantFor(this.cfg.products.tag, n.dataset.sizePrice, st.material);
        n.textContent = v ? money(v.price) : '';
      });
      const r = this.lineItems(st), out = this.querySelector('[data-total]');
      if (r.error) { out.textContent = r.error; return; }
      const full = this.total(r.items), sp = this.specialOff(r.items);
      out.innerHTML = sp.off ? `<s>${money(full)}</s> ${money(full - sp.off)}` : money(full);
      const note = this.querySelector('[data-special]');
      if (note) {
        const each = money(SPECIAL.offEach);
        note.textContent = sp.off ? `Stall pack special: ${each} off each ${SPECIAL.size} tag, applied at checkout.`
          : st.size === SPECIAL.size ? `Stall pack special: take ${SPECIAL.min} or more ${SPECIAL.size} tags and save ${each} on each.` : '';
        note.hidden = !note.textContent;
      }
    }
    async addToCart() {
      const st = this.state(), r = this.lineItems(st), btn = this.form.querySelector('[type=submit]'), msg = this.querySelector('[data-message]');
      if (r.error) { msg.textContent = r.error; return; }
      const prob = this.priceProblem(st);
      if (prob && prob.block) { msg.textContent = prob.text; return; }
      if (this.busy) return;
      this.busy = true; btn.disabled = true; msg.textContent = '';
      try {
        const res = await fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: r.items }),
        });
        if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
        window.location.href = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart';
      } catch (e) {
        msg.textContent = e.message; this.busy = false; btn.disabled = false;
      }
    }
  }
  if (!customElements.get('price-tag-builder')) customElements.define('price-tag-builder', PriceTagBuilder);
})();
